import { useEffect, useState } from "react";

import {
  ErrorApi,
  iniciarRecomposicion,
  listarSalidasFlyer,
  obtenerEstadoGeneracion,
} from "../api/client";
import {
  FORMATOS_FLYER,
  type FormatoFlyer,
  type Job,
  type ProgresoGeneracion,
  type Proyecto,
  type SalidaFlyer,
} from "../api/types";
import estilos from "./PanelFormatos.module.css";

interface Props {
  token: string;
  proyecto: Proyecto;
  // El padre (VistaProyecto) refresca el proyecto (gastado_usd/remanente_usd
  // cambian con cada recomposicion, ver backend/app/budget.py) cuando un job
  // de recomposicion termina.
  onCambioPresupuesto?: () => void;
}

const INTERVALO_SONDEO_MS = 2000;

const MENSAJE_CUOTA =
  "Se agoto la cuota de la suscripcion de Claude compartida por el equipo; reintenta mas tarde.";
const MENSAJE_PRESUPUESTO = "Se alcanzo el tope de presupuesto del proyecto.";

function mensajeDeError(err: unknown, generico: string): string {
  if (err instanceof ErrorApi) return err.message;
  if (err instanceof Error) return err.message;
  return generico;
}

// `JobOut` (backend/app/schemas.py) no expone `input` -- solo `output`, que
// `execute_recomposition` llena recien al terminar con exito (ver
// generation_worker.py). Mientras el job esta en curso no hay forma de leer
// el formato elegido desde el job: el titulo de "Recomponiendo a..." usa
// `formatoElegido` (estado local) como resguardo.
function formatoDeSalida(job: Job): string | null {
  const salida = (job.output as { format?: string } | null)?.format;
  return typeof salida === "string" ? salida : null;
}

// Re-composicion por formato (M6, POST /projects/{id}/recompose): re-disena
// el flyer YA generado para otro aspecto (1:1/4:5/9:16/16:9), preservando el
// texto literal -- el formato ORIGINAL del proyecto (`Project.format`
// /`flyer_document`/`png_url`) NUNCA se toca, el resultado se guarda aparte
// en un `FlyerOutput` por formato (ver backend/app/models.py::FlyerOutput).
// Comparte el mismo cupo de "un job activo por proyecto" que /generate: el
// progreso se sondea con el mismo GET /generation que usa la generacion
// primaria (devuelve el job mas reciente sin filtrar por kind).
export function PanelFormatos({
  token,
  proyecto,
  onCambioPresupuesto,
}: Props): JSX.Element {
  const formatosDisponibles = FORMATOS_FLYER.filter(
    (f) => f !== proyecto.format,
  );

  const [salidas, setSalidas] = useState<SalidaFlyer[]>([]);
  const [cargandoSalidas, setCargandoSalidas] = useState(true);
  const [errorSalidas, setErrorSalidas] = useState<string | null>(null);
  const [formatoVisible, setFormatoVisible] = useState<string>(proyecto.format);

  const [formatoElegido, setFormatoElegido] = useState<FormatoFlyer>(
    formatosDisponibles[0] ?? proyecto.format,
  );
  const [enviando, setEnviando] = useState(false);
  const [job, setJob] = useState<Job | null>(null);
  const [progreso, setProgreso] = useState<ProgresoGeneracion | null>(null);
  const [enCurso, setEnCurso] = useState(false);
  const [errorRecomposicion, setErrorRecomposicion] = useState<string | null>(
    null,
  );
  const [mensajeFinal, setMensajeFinal] = useState<string | null>(null);

  const cargarSalidas = async (): Promise<SalidaFlyer[]> => {
    const datos = await listarSalidasFlyer(token, proyecto.id);
    setSalidas(datos);
    return datos;
  };

  // Carga inicial de las salidas por formato + deteccion de una
  // recomposicion YA en curso (p.ej. si el usuario recargo la pagina a mitad
  // de un job) via el job mas reciente.
  useEffect(() => {
    let cancelado = false;
    setCargandoSalidas(true);
    cargarSalidas()
      .then(() => {
        if (!cancelado) setErrorSalidas(null);
      })
      .catch((err: unknown) => {
        if (!cancelado) {
          setErrorSalidas(
            mensajeDeError(err, "No se pudieron cargar los formatos"),
          );
        }
      })
      .finally(() => {
        if (!cancelado) setCargandoSalidas(false);
      });

    obtenerEstadoGeneracion(token, proyecto.id)
      .then((estado) => {
        if (cancelado || !estado.job) return;
        if (
          estado.job.kind === "recomposition" &&
          (estado.job.status === "queued" || estado.job.status === "running")
        ) {
          setJob(estado.job);
          setProgreso(estado.progress);
          setEnCurso(true);
        }
      })
      .catch(() => {
        // Sin job previo detectable: no es bloqueante, el usuario puede
        // igual disparar una recomposicion nueva.
      });

    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, proyecto.id]);

  // Bucle de sondeo mientras hay un job de recomposicion en curso (mismo
  // patron de re-armado que useProyecto.ts: cada vez que `job` cambia con un
  // estado no terminal, se agenda el siguiente tick).
  useEffect(() => {
    if (!enCurso) return;
    const temporizador = window.setTimeout(async () => {
      try {
        const estado = await obtenerEstadoGeneracion(token, proyecto.id);
        setJob(estado.job);
        setProgreso(estado.progress);
        const status = estado.job?.status;
        if (status === "queued" || status === "running") return;

        setEnCurso(false);
        if (status === "succeeded") {
          const datos = await cargarSalidas();
          const terminado = estado.job ? formatoDeSalida(estado.job) : null;
          if (terminado && datos.some((s) => s.format === terminado)) {
            setFormatoVisible(terminado);
          }
          onCambioPresupuesto?.();
        } else if (status === "quota_exceeded") {
          setMensajeFinal(MENSAJE_CUOTA);
        } else if (status === "budget_exceeded") {
          setMensajeFinal(MENSAJE_PRESUPUESTO);
        } else if (status === "failed") {
          const detalle = (estado.job?.error as { message?: string } | null)
            ?.message;
          setErrorRecomposicion(
            detalle ?? "La recomposicion termino con error.",
          );
        }
      } catch (err) {
        setEnCurso(false);
        setErrorRecomposicion(
          mensajeDeError(
            err,
            "No se pudo consultar el estado de la recomposicion",
          ),
        );
      }
    }, INTERVALO_SONDEO_MS);
    return () => window.clearTimeout(temporizador);
  }, [enCurso, job, token, proyecto.id, onCambioPresupuesto]);

  const iniciar = async (): Promise<void> => {
    if (enviando || enCurso) return;
    setEnviando(true);
    setErrorRecomposicion(null);
    setMensajeFinal(null);
    try {
      const nuevoJob = await iniciarRecomposicion(
        token,
        proyecto.id,
        formatoElegido,
      );
      setJob(nuevoJob);
      setProgreso(null);
      setEnCurso(true);
    } catch (err) {
      setErrorRecomposicion(
        mensajeDeError(err, "No se pudo iniciar la recomposicion"),
      );
    } finally {
      setEnviando(false);
    }
  };

  const puedeRecomponer =
    proyecto.flyer_document !== null &&
    proyecto.estado !== "generando" &&
    !enCurso &&
    !enviando &&
    formatosDisponibles.length > 0;

  const pct =
    progreso && Number.isFinite(progreso.pct)
      ? Math.round(progreso.pct * 100)
      : null;
  const mensajeProgreso =
    progreso?.mensaje ?? progreso?.stage ?? "Preparando...";

  const salidaSeleccionada =
    formatoVisible === proyecto.format
      ? null
      : (salidas.find((s) => s.format === formatoVisible) ?? null);
  const urlVisible =
    formatoVisible === proyecto.format
      ? proyecto.png_url
      : (salidaSeleccionada?.png_url ?? null);

  return (
    <section className={estilos.tarjeta} aria-labelledby="titulo-formatos">
      <header className={estilos.encabezado}>
        <h3 id="titulo-formatos">Formatos del flyer</h3>
        <p className={estilos.subtitulo}>
          El formato original ({proyecto.format}) no se modifica: recomponer
          genera el mismo diseño para otro aspecto y lo guarda aparte.
        </p>
      </header>

      {proyecto.flyer_document === null && (
        <p className={estilos.mensaje}>
          Genera el formato original primero para poder recomponer otros
          formatos.
        </p>
      )}

      <div className={estilos.controlRecomponer}>
        <div className={estilos.campoFormato}>
          <label htmlFor="formato-recomponer">Recomponer a</label>
          <select
            id="formato-recomponer"
            value={formatoElegido}
            onChange={(e) => setFormatoElegido(e.target.value as FormatoFlyer)}
            disabled={!puedeRecomponer}
          >
            {formatosDisponibles.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="boton boton-secundario"
          onClick={() => void iniciar()}
          disabled={!puedeRecomponer}
        >
          {enCurso || enviando ? "Recomponiendo..." : "Recomponer"}
        </button>
      </div>

      {enCurso && (
        <div
          className={estilos.progreso}
          role="status"
          aria-live="polite"
          aria-labelledby="titulo-progreso-recomposicion"
        >
          <h4
            id="titulo-progreso-recomposicion"
            className={estilos.tituloProgreso}
          >
            Recomponiendo a {(job && formatoDeSalida(job)) ?? formatoElegido}
          </h4>
          <div className={estilos.barra}>
            <div
              className={`${estilos.relleno} ${pct === null ? estilos.indeterminado : ""}`}
              style={pct !== null ? { width: `${pct}%` } : undefined}
            />
          </div>
          <p className={estilos.mensaje}>
            {mensajeProgreso}
            {pct !== null ? ` (${pct}%)` : ""}
          </p>
        </div>
      )}

      {mensajeFinal && (
        <p className={estilos.avisoLimite} role="status">
          {mensajeFinal}
        </p>
      )}
      {errorRecomposicion && (
        <p className={estilos.mensajeError} role="alert">
          {errorRecomposicion}
        </p>
      )}

      {cargandoSalidas && (
        <p className={estilos.mensaje}>Cargando formatos...</p>
      )}
      {errorSalidas && !cargandoSalidas && (
        <p className={estilos.mensajeError} role="alert">
          {errorSalidas}
        </p>
      )}

      {!cargandoSalidas && (proyecto.png_url || salidas.length > 0) && (
        <ul className={estilos.galeria} aria-label="Formatos disponibles">
          {proyecto.png_url && (
            <li>
              <button
                type="button"
                className={`${estilos.miniaturaBoton} ${
                  formatoVisible === proyecto.format
                    ? estilos.miniaturaActiva
                    : ""
                }`}
                aria-pressed={formatoVisible === proyecto.format}
                onClick={() => setFormatoVisible(proyecto.format)}
              >
                <img
                  src={proyecto.png_url}
                  alt=""
                  className={estilos.miniatura}
                />
                <span className={estilos.etiquetaMiniatura}>
                  {proyecto.format} (original)
                </span>
              </button>
            </li>
          )}
          {salidas.map((s) => (
            <li key={s.format}>
              <button
                type="button"
                className={`${estilos.miniaturaBoton} ${
                  formatoVisible === s.format ? estilos.miniaturaActiva : ""
                }`}
                aria-pressed={formatoVisible === s.format}
                onClick={() => setFormatoVisible(s.format)}
              >
                {s.png_url ? (
                  <img src={s.png_url} alt="" className={estilos.miniatura} />
                ) : (
                  <span className={estilos.miniaturaVacia}>Sin imagen</span>
                )}
                <span className={estilos.etiquetaMiniatura}>{s.format}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {!cargandoSalidas && !proyecto.png_url && salidas.length === 0 && (
        <p className={estilos.mensaje}>
          Todavia no hay ningun formato generado.
        </p>
      )}

      {urlVisible && (
        <div className={estilos.visor}>
          <img
            src={urlVisible}
            alt={`Flyer en formato ${formatoVisible}`}
            className={estilos.imagenGrande}
          />
          <a
            className="boton boton-secundario"
            href={urlVisible}
            target="_blank"
            rel="noreferrer"
          >
            Abrir imagen en pestana nueva
          </a>
        </div>
      )}
    </section>
  );
}
